// OSIS - Calcule Structurale
import React, { useState } from 'react';
import { 
  Calculator,
  Activity,
  Wind,
  Shield,
  Zap,
  Thermometer,
  Weight,
  Ruler,
  AlertTriangle,
  CheckCircle,
  FileText,
  Download,
  Upload,
  Play,
  Pause,
  RotateCcw,
  Settings,
  Eye,
  Save
} from 'lucide-react';
import Card from '../../components/Card';

const StructuralCalculations = () => {
  const [activeCalculation, setActiveCalculation] = useState('wind-load');
  const [calculationRunning, setCalculationRunning] = useState(false);

  // Tipuri de calcule disponibile
  const calculationTypes = [
    {
      id: 'wind-load',
      name: 'Sarcină Vânt',
      icon: Wind,
      description: 'Calculare presiune vânt pe proiecte și fațade',
      complexity: 'Medie',
      standards: ['SR EN 1991-1-4', 'NP 082-04']
    },
    {
      id: 'thermal',
      name: 'Transmitanță Termică',
      icon: Thermometer,
      description: 'Coeficient U pentru eficiența energetică',
      complexity: 'Standard',
      standards: ['SR EN ISO 6946', 'C 107/7-2015']
    },
    {
      id: 'structural',
      name: 'Rezistență Structurală',
      icon: Shield,
      description: 'Calculare capacitate portantă profile',
      complexity: 'Complexă',
      standards: ['SR EN 1993', 'SR EN 1999']
    },
    {
      id: 'deflection',
      name: 'Săgeți Admise',
      icon: Activity,
      description: 'Verificare deformații la încărcări',
      complexity: 'Medie',
      standards: ['SR EN 13830', 'prEN 14351-1']
    },
    {
      id: 'seismic',
      name: 'Rezistență Seismică',
      icon: Zap,
      description: 'Comportament la cutremur - P100-1',
      complexity: 'Complexă',
      standards: ['P100-1/2013', 'SR EN 1998']
    },
    {
      id: 'security',
      name: 'lucrări Blindate',
      icon: Shield,
      description: 'Clasă rezistență la efracție',
      complexity: 'Standard',
      standards: ['SR EN 1627-1630', 'SR EN 356']
    }
  ];

  // Calcule recente
  const recentCalculations = [
    {
      id: 'CALC-2024-023',
      project: 'PROJ-2024-015',
      type: 'Sarcină Vânt',
      element: 'suprafață mare 200x300cm',
      client: 'SC Luxury Construct',
      results: {
        windPressure: '1.2 kN/m²',
        maxDeflection: '8.5 mm',
        safetyFactor: '2.1',
        status: 'Conformă'
      },
      date: '2024-09-05',
      engineer: 'Ionel Filip',
      status: 'Finalizat',
      confidence: 'Ridicată'
    },
    {
      id: 'CALC-2024-024',
      project: 'PROJ-2024-017',
      type: 'lucrări Blindate',
      element: 'pardoseală heavy duty clasa III 90x200cm',
      client: 'SC Security Plus',
      results: {
        resistanceClass: 'RC3',
        impactResistance: '≥450 J',
        toolResistance: '≥5 min',
        status: 'Conformă'
      },
      date: '2024-09-04',
      engineer: 'Ionel Filip',
      status: 'Aprobat',
      confidence: 'Ridicată'
    },
    {
      id: 'CALC-2024-025',
      project: 'PROJ-2024-018',
      type: 'Rezistență Structurală',
      element: 'Curtain wall 120m²',
      client: 'Business Center SRL',
      results: {
        maxStress: '85 N/mm²',
        admissibleStress: '235 N/mm²',
        utilizationRatio: '0.36',
        status: 'Conformă'
      },
      date: '2024-09-03',
      engineer: 'Ionel Filip',
      status: 'În revizuire',
      confidence: 'Medie'
    }
  ];

  // Parametri pentru calculul curent
  const [calculationParams, setCalculationParams] = useState({
    windLoad: {
      height: '15',
      width: '300',
      buildingHeight: '25',
      terrain: 'urban',
      windZone: '2',
      exposure: 'normal'
    }
  });

  // Statistici
  const stats = [
    {
      title: 'Calcule Finalizate',
      value: '47',
      change: '+8 luna aceasta',
      changeType: 'positive',
      icon: CheckCircle,
      color: 'success'
    },
    {
      title: 'În Progres',
      value: '5',
      change: 'Ionel Filip - responsabil',
      changeType: 'neutral',
      icon: Calculator,
      color: 'primary'
    },
    {
      title: 'Conformitate',
      value: '96%',
      change: 'Rata de succes',
      changeType: 'positive',
      icon: Shield,
      color: 'info'
    },
    {
      title: 'Timp Mediu',
      value: '2.3h',
      change: 'Per calcul complex',
      changeType: 'neutral',
      icon: Activity,
      color: 'warning'
    }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'Finalizat': return 'success';
      case 'Aprobat': return 'success';
      case 'În revizuire': return 'warning';
      case 'În progres': return 'primary';
      case 'Eroare': return 'error';
      default: return 'gray';
    }
  };

  const getConfidenceColor = (confidence) => {
    switch(confidence) {
      case 'Ridicată': return 'success';
      case 'Medie': return 'warning';
      case 'Scăzută': return 'error';
      default: return 'gray';
    }
  };

  const getComplexityColor = (complexity) => {
    switch(complexity) {
      case 'Complexă': return 'error';
      case 'Medie': return 'warning';
      case 'Standard': return 'success';
      default: return 'gray';
    }
  };

  const handleRunCalculation = () => {
    setCalculationRunning(true);
    // Simulare calcul
    setTimeout(() => {
      setCalculationRunning(false);
    }, 3000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Calculator className="w-8 h-8 mr-3 text-primary-600" />
            Calcule Structurale
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Analize și verificări conform standardelor românești și europene - <span className="font-medium text-primary-600">Ionel Filip</span>
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <button className="btn btn-secondary">
            <Upload className="w-4 h-4 mr-2" />
            Import Date
          </button>
          
          <button className="btn btn-primary">
            <FileText className="w-4 h-4 mr-2" />
            Calcul Nou
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="relative overflow-hidden">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
                  <p className={`text-sm mt-1 ${
                    stat.changeType === 'positive' ? 'text-success' :
                    stat.changeType === 'negative' ? 'text-error' : 
                    'text-gray-500 dark:text-gray-400'
                  }`}>
                    {stat.change}
                  </p>
                </div>
                <div className={`p-3 rounded-xl bg-${stat.color}-100 dark:bg-${stat.color}-900/30`}>
                  <Icon className={`w-6 h-6 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Calculation Types */}
        <div>
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tipuri de Calcule</h3>
            
            <div className="space-y-3">
              {calculationTypes.map((calc) => {
                const Icon = calc.icon;
                return (
                  <button
                    key={calc.id}
                    onClick={() => setActiveCalculation(calc.id)}
                    className={`w-full p-3 rounded-xl text-left transition-colors ${
                      activeCalculation === calc.id
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-2 border-primary-200 dark:border-primary-800'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center">
                        <Icon className="w-5 h-5 mr-2" />
                        <span className="font-medium text-sm">{calc.name}</span>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getComplexityColor(calc.complexity)}-100 text-${getComplexityColor(calc.complexity)}-700 dark:bg-${getComplexityColor(calc.complexity)}-900/30 dark:text-${getComplexityColor(calc.complexity)}-300`}>
                        {calc.complexity}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      {calc.description}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {calc.standards.map((standard, index) => (
                        <span key={index} className="px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                          {standard}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Calculation Interface */}
        <div className="xl:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <Wind className="w-5 h-5 mr-2 text-primary-600" />
                Calculator: Sarcină Vânt
              </h3>
              <div className="flex items-center space-x-2">
                <button className="btn btn-sm btn-secondary">
                  <Settings className="w-4 h-4 mr-1" />
                  Parametri
                </button>
                <button 
                  onClick={handleRunCalculation}
                  disabled={calculationRunning}
                  className="btn btn-sm btn-primary"
                >
                  {calculationRunning ? (
                    <Pause className="w-4 h-4 mr-1" />
                  ) : (
                    <Play className="w-4 h-4 mr-1" />
                  )}
                  {calculationRunning ? 'Calculează...' : 'Rulează'}
                </button>
              </div>
            </div>

            {/* Input Parameters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-white">Dimensiuni Element</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Înălțime (cm)
                  </label>
                  <input
                    type="number"
                    value={calculationParams.windLoad.height}
                    onChange={(e) => setCalculationParams({
                      ...calculationParams,
                      windLoad: { ...calculationParams.windLoad, height: e.target.value }
                    })}
                    className="form-input"
                    placeholder="150"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Lățime (cm)
                  </label>
                  <input
                    type="number"
                    value={calculationParams.windLoad.width}
                    onChange={(e) => setCalculationParams({
                      ...calculationParams,
                      windLoad: { ...calculationParams.windLoad, width: e.target.value }
                    })}
                    className="form-input"
                    placeholder="300"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Înălțime Clădire (m)
                  </label>
                  <input
                    type="number"
                    value={calculationParams.windLoad.buildingHeight}
                    onChange={(e) => setCalculationParams({
                      ...calculationParams,
                      windLoad: { ...calculationParams.windLoad, buildingHeight: e.target.value }
                    })}
                    className="form-input"
                    placeholder="25"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-white">Condiții de Expunere</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tipul Terenului
                  </label>
                  <select 
                    value={calculationParams.windLoad.terrain}
                    onChange={(e) => setCalculationParams({
                      ...calculationParams,
                      windLoad: { ...calculationParams.windLoad, terrain: e.target.value }
                    })}
                    className="form-select"
                  >
                    <option value="open">Teren deschis</option>
                    <option value="suburban">Suburban</option>
                    <option value="urban">Urban</option>
                    <option value="industrial">Industrial</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Zona de Vânt
                  </label>
                  <select 
                    value={calculationParams.windLoad.windZone}
                    onChange={(e) => setCalculationParams({
                      ...calculationParams,
                      windLoad: { ...calculationParams.windLoad, windZone: e.target.value }
                    })}
                    className="form-select"
                  >
                    <option value="1">Zona I (22 m/s)</option>
                    <option value="2">Zona II (25 m/s)</option>
                    <option value="3">Zona III (28 m/s)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Categoria Expunerii
                  </label>
                  <select 
                    value={calculationParams.windLoad.exposure}
                    onChange={(e) => setCalculationParams({
                      ...calculationParams,
                      windLoad: { ...calculationParams.windLoad, exposure: e.target.value }
                    })}
                    className="form-select"
                  >
                    <option value="normal">Normală</option>
                    <option value="wind-exposed">Expusă la vânt</option>
                    <option value="sheltered">Adăpostită</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="font-medium text-gray-900 dark:text-white mb-4">Rezultate Calcul</h4>
              
              {calculationRunning ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                  <span className="ml-4 text-gray-600 dark:text-gray-400">Calculând sarcina de vânt...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div className="flex items-center justify-between mb-1">
                      <Wind className="w-5 h-5 text-primary-600" />
                      <span className="text-xs font-medium text-success px-2 py-1 bg-success-100 dark:bg-success-900/30 rounded-full">
                        OK
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Presiune Vânt</p>
                    <p className="text-lg font-bold text-primary-600">1.2 kN/m²</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">Max admisă: 1.5 kN/m²</p>
                  </div>
                  
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div className="flex items-center justify-between mb-1">
                      <Activity className="w-5 h-5 text-warning-600" />
                      <span className="text-xs font-medium text-success px-2 py-1 bg-success-100 dark:bg-success-900/30 rounded-full">
                        OK
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Săgeată Max</p>
                    <p className="text-lg font-bold text-warning-600">8.5 mm</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">Limită: L/300 (15 mm)</p>
                  </div>
                  
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div className="flex items-center justify-between mb-1">
                      <Shield className="w-5 h-5 text-success-600" />
                      <span className="text-xs font-medium text-success px-2 py-1 bg-success-100 dark:bg-success-900/30 rounded-full">
                        SIGUR
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Factor Siguranță</p>
                    <p className="text-lg font-bold text-success-600">2.1</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">Minim necesar: 1.5</p>
                  </div>
                  
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div className="flex items-center justify-between mb-1">
                      <CheckCircle className="w-5 h-5 text-success-600" />
                      <span className="text-xs font-medium text-success px-2 py-1 bg-success-100 dark:bg-success-900/30 rounded-full">
                        CONFORMĂ
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Status Final</p>
                    <p className="text-lg font-bold text-success-600">Aprobat</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">SR EN 1991-1-4</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button className="btn btn-secondary">
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </button>
                <button className="btn btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  Salvează Calcul
                </button>
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Calculations */}
        <div>
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Calcule Recente</h3>
              <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                Vezi toate
              </button>
            </div>
            
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {recentCalculations.map((calc, index) => (
                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{calc.id}</span>
                    <div className="flex items-center space-x-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getStatusColor(calc.status)}-100 text-${getStatusColor(calc.status)}-700 dark:bg-${getStatusColor(calc.status)}-900/30 dark:text-${getStatusColor(calc.status)}-300`}>
                        {calc.status}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getConfidenceColor(calc.confidence)}-100 text-${getConfidenceColor(calc.confidence)}-700 dark:bg-${getConfidenceColor(calc.confidence)}-900/30 dark:text-${getConfidenceColor(calc.confidence)}-300`}>
                        {calc.confidence}
                      </span>
                    </div>
                  </div>
                  
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">{calc.type}</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{calc.element}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mb-2">{calc.client}</p>
                  
                  <div className="text-xs text-gray-500 dark:text-gray-500 space-y-1">
                    {Object.entries(calc.results).map(([key, value], resIndex) => (
                      <div key={resIndex} className="flex justify-between">
                        <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}:</span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-xs text-gray-500 dark:text-gray-500">
                      {new Date(calc.date).toLocaleDateString('ro-RO')}
                    </span>
                    <div className="flex items-center space-x-1">
                      <button className="p-1 text-gray-600 hover:text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded">
                        <Eye className="w-3 h-3" />
                      </button>
                      <button className="p-1 text-gray-600 hover:text-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-900/30 rounded">
                        <Download className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Standards Reference */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-info-600" />
          Standarde și Norme de Referință
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-info-50 hover:bg-info-100 dark:bg-info-900/30 dark:hover:bg-info-900/50 rounded-xl transition-colors">
            <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2">Sarcini Climatice</h4>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <li>• SR EN 1991-1-4 (Acțiuni vânt)</li>
              <li>• SR EN 1991-1-3 (Sarcini zăpadă)</li>
              <li>• NP 082-04 (Normativ românesc)</li>
            </ul>
          </div>
          
          <div className="p-4 bg-success-50 hover:bg-success-100 dark:bg-success-900/30 dark:hover:bg-success-900/50 rounded-xl transition-colors">
            <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2">Calcule Structurale</h4>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <li>• SR EN 1993 (Structuri oțel)</li>
              <li>• SR EN 1999 (Structuri aluminiu)</li>
              <li>• SR EN 13830 (Fațade continua)</li>
            </ul>
          </div>
          
          <div className="p-4 bg-warning-50 hover:bg-warning-100 dark:bg-warning-900/30 dark:hover:bg-warning-900/50 rounded-xl transition-colors">
            <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2">Performanțe Termice</h4>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <li>• SR EN ISO 6946 (Transmitanță)</li>
              <li>• C 107/7-2015 (Normativ energie)</li>
              <li>• SR EN 14351-1 (pardoseli)</li>
            </ul>
          </div>
          
          <div className="p-4 bg-error-50 hover:bg-error-100 dark:bg-error-900/30 dark:hover:bg-error-900/50 rounded-xl transition-colors">
            <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2">Securitate</h4>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <li>• SR EN 1627-1630 (Efracție)</li>
              <li>• SR EN 356 (beton șlefuit)</li>
              <li>• P100-1/2013 (Seismic)</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default StructuralCalculations;